import {
  GenerateLearnerMixTopicMutation,
  GenerateLearnerMixTopicMutationVariables,
  PollGeneratedImageQuery,
  PollGeneratedImageQueryVariables,
} from "@outschool/gql-frontend-generated";
import {
  ApolloError,
  gql,
  useLazyQuery,
  useMutation,
} from "@outschool/ui-apollo";
import { useCallback, useEffect, useRef, useState } from "react";

export const GENERATE_LEARNER_MIX_TOPIC_MUTATION = gql`
  mutation GenerateLearnerMixTopic($sourceTopics: [String!]!) {
    generateLearnerMixTopic(sourceTopics: $sourceTopics) {
      sourceTopics
      title
      description
      generationId
    }
  }
`;

export const POLL_GENERATED_IMAGE_QUERY = gql`
  query PollGeneratedImage($generationId: String!, $title: String!) {
    pollGeneratedImage(generationId: $generationId, title: $title) {
      imageUrl
    }
  }
`;

const IMAGE_POLL_INTERVAL_MS = 2000;

export interface GeneratedMixTopicResult {
  sourceTopics: string[];
  title: string;
  description: string;
  generationId: string;
}

interface UseGenerateMixTopicOptions {
  onTopicGenerated?: (topic: GeneratedMixTopicResult) => void;
  onImageReady?: (imageUrl: string) => void;
  onError?: () => void;
}

interface UseGenerateMixTopicReturn {
  generateMixTopic: (
    sourceTopics: string[],
  ) => Promise<GeneratedMixTopicResult | undefined>;
  isGeneratingTopic: boolean;
  isPollingImage: boolean;
  generatingError: ApolloError | undefined;
  pollingError: ApolloError | undefined;
  reset: () => void;
}

/**
 * Custom hook for generating mix topics with async image generation.
 *
 * Flow:
 * 1. Call generateMixTopic(sourceTopics)
 * 2. OpenAI generates title/description → onTopicGenerated callback
 * 3. Starts polling Leonardo for image → onImageReady callback when done
 */
export function useGenerateMixTopic(
  options: UseGenerateMixTopicOptions = {},
): UseGenerateMixTopicReturn {
  const { onTopicGenerated, onImageReady, onError } = options;

  const [pendingGeneration, setPendingGeneration] = useState<{
    generationId: string;
    title: string;
  } | null>(null);

  const hasNotifiedErrorRef = useRef(false);

  const [
    generateLearnerMixTopicMutation,
    {
      loading: isGeneratingTopic,
      error: generatingError,
      reset: resetMutation,
    },
  ] = useMutation<
    GenerateLearnerMixTopicMutation,
    GenerateLearnerMixTopicMutationVariables
  >(GENERATE_LEARNER_MIX_TOPIC_MUTATION);

  const [
    pollGeneratedImage,
    {
      data: pollData,
      loading: isPollingImage,
      error: pollingError,
      startPolling,
      stopPolling,
    },
  ] = useLazyQuery<PollGeneratedImageQuery, PollGeneratedImageQueryVariables>(
    POLL_GENERATED_IMAGE_QUERY,
    {
      /**
       * Disable Apollo cache for this query.
       * 1. This query has a side effect: it uploads the image to S3 when ready.
       *    Without this, Apollo would return cached results instead of hitting
       *    the server, and we'd never trigger the S3 upload.
       * 2. We need fresh data on each poll to check if Leonardo has finished
       *    generating the image.
       */
      fetchPolicy: "network-only",
    },
  );

  useEffect(() => {
    const imageUrl = pollData?.pollGeneratedImage?.imageUrl;
    if (imageUrl && pendingGeneration) {
      stopPolling();
      setPendingGeneration(null);
      onImageReady?.(imageUrl);
    }
  }, [pollData, pendingGeneration, stopPolling, onImageReady]);

  // Handle poll error - stop polling and notify
  useEffect(() => {
    if (pollingError && pendingGeneration && !hasNotifiedErrorRef.current) {
      stopPolling();
      setPendingGeneration(null);
      hasNotifiedErrorRef.current = true;
      onError?.();
    }
  }, [pollingError, pendingGeneration, stopPolling, onError]);

  // Handle generation error
  useEffect(() => {
    if (generatingError && !hasNotifiedErrorRef.current) {
      hasNotifiedErrorRef.current = true;
      onError?.();
    }
  }, [generatingError, onError]);

  const generateMixTopic = useCallback(
    async (
      sourceTopics: string[],
    ): Promise<GeneratedMixTopicResult | undefined> => {
      hasNotifiedErrorRef.current = false;

      const result = await generateLearnerMixTopicMutation({
        variables: { sourceTopics },
      });

      const generatedTopic = result.data?.generateLearnerMixTopic;

      if (generatedTopic) {
        // Notify that topic is generated (title/description ready)
        onTopicGenerated?.(generatedTopic);

        // Start polling for the image
        setPendingGeneration({
          generationId: generatedTopic.generationId,
          title: generatedTopic.title,
        });
        pollGeneratedImage({
          variables: {
            generationId: generatedTopic.generationId,
            title: generatedTopic.title,
          },
        });
        startPolling(IMAGE_POLL_INTERVAL_MS);
      }

      return generatedTopic;
    },
    [
      generateLearnerMixTopicMutation,
      onTopicGenerated,
      pollGeneratedImage,
      startPolling,
    ],
  );

  const reset = useCallback(() => {
    resetMutation();
    stopPolling();
    setPendingGeneration(null);
    hasNotifiedErrorRef.current = false;
  }, [resetMutation, stopPolling]);

  return {
    generateMixTopic,
    isGeneratingTopic,
    isPollingImage,
    generatingError,
    pollingError,
    reset,
  };
}
